import {FC} from 'react';
import styled from 'styled-components';

import {AddressLookupResult, HoobiizVendorLocation} from '@shared/dynamo_model';
import {capitalize} from '@shared/lib/format_utils';
import {removeUndefined} from '@shared/lib/type_utils';

import {EmptyFragment} from '@shared-frontend/lib/react';

import {HoobiizGoogleMaps} from '@src/components/ui/hoobiiz_google_maps';
import {DAYS, formatTimeOfDay} from '@src/lib/hoobiiz_week_periods';

interface HoobiizContactAndAccessSectionProps extends Partial<HoobiizVendorLocation> {}

export function addressToLines(address: AddressLookupResult): {line1: string; line2: string} {
  const {streetNumber, route, postalCode, city, country} = address.addressDetails;
  const chunk1 = removeUndefined([streetNumber, route]).join(' ');
  const chunk2 = removeUndefined([postalCode, city]).join(' ');
  const chunk3 = removeUndefined([country]).join(' ');
  return {line1: chunk1, line2: [chunk2, chunk3].filter(s => s.length > 0).join(', ')};
}

export const HoobiizContactAndAccessSection: FC<HoobiizContactAndAccessSectionProps> = props => {
  const {
    name,
    hideName,
    phone,
    hidePhone = true,
    address,
    hideAddress,
    openingHours,
    hideOpeningHours,
  } = props;
  const addressLines = address ? addressToLines(address) : {line1: undefined, line2: undefined};
  const showLeft = !(hideAddress && hidePhone && hideOpeningHours);
  return (
    <Wrapper>
      {showLeft ? (
        <Left>
          {address && !(hideAddress && hidePhone) ? (
            <LeftCol1>
              {hideAddress ? (
                EmptyFragment
              ) : (
                <Section>
                  <Subtitle>Adresse</Subtitle>
                  <ContactLines>
                    {hideName ? EmptyFragment : <div>{name}</div>}
                    <div>{addressLines.line1 ?? 'Adresse non renseignée'}</div>
                    <div>{addressLines.line2}</div>
                  </ContactLines>
                </Section>
              )}
              {hidePhone ? (
                EmptyFragment
              ) : (
                <Section>
                  <Subtitle>Contact</Subtitle>
                  <div>{phone}</div>
                </Section>
              )}
            </LeftCol1>
          ) : (
            EmptyFragment
          )}
          {hideOpeningHours ? (
            EmptyFragment
          ) : (
            <LeftCol2>
              <Subtitle>Heures d'ouverture</Subtitle>
              <Hours>
                {DAYS.map(({label, value}) => {
                  const times = openingHours?.weekdays[value] ?? [];
                  return (
                    <HoursLine key={value}>
                      <div>{capitalize(label.toLowerCase())}</div>
                      {times.length === 0 ? (
                        <div>Fermé</div>
                      ) : (
                        <div>
                          {times.map(t => (
                            <div key={formatTimeOfDay(t)}>{formatTimeOfDay(t)}</div>
                          ))}
                        </div>
                      )}
                    </HoursLine>
                  );
                })}
              </Hours>
            </LeftCol2>
          )}
        </Left>
      ) : (
        EmptyFragment
      )}
      {IS_BROWSER && address && !hideAddress ? (
        <Right>
          <HoobiizGoogleMaps name={hideName ? undefined : name} address={address} />
        </Right>
      ) : (
        EmptyFragment
      )}
    </Wrapper>
  );
};

HoobiizContactAndAccessSection.displayName = 'HoobiizContactAndAccessSection';

const Subtitle = styled.div`
  font-size: 17px;
  font-weight: 700;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  white-space: nowrap;
`;

const Left = styled.div`
  width: 0;
  min-width: 400px;
  flex-grow: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
`;

const LeftCol1 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 0;
  flex-grow: 1;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: normal;
`;

const LeftCol2 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 0;
  flex-grow: 1;
`;

const Right = styled.div`
  width: 0;
  min-width: 400px;
  flex-grow: 1;
`;

const Hours = styled.div`
  display: flex;
  flex-direction: column;
  border-top: solid 1px #eee;
  max-width: 300px;
`;

const HoursLine = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  border-bottom: solid 1px #eee;
  padding: 4px 0;
`;
